import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DocumentDTO, PersonDTO } from '@almanac/shared';
import { useDocuments } from '../lib/documents.js';
import { useCurrentUser } from '../lib/me.js';
import { useNowMinute, useObligations } from '../lib/obligations.js';
import {
  describeOccasion,
  personDocuments,
  personOccasions,
  usePeople,
  useUpdatePerson,
} from '../lib/people.js';
import { PersonAvatar } from './PersonAvatar.js';

interface Props {
  person: PersonDTO;
  onClose: () => void;
  /** The Edit action — the host opens the PersonEditSheet. */
  onEdit: () => void;
  /** Tapping an occasion row — the host (PersonCard) swaps in that
   *  entry's preview in place, so closing it comes back here instead
   *  of stranding the user in the Obligations tool. */
  onOpenOccasion: (id: string) => void;
  /** Tapping a key-document row — same in-place pattern. */
  onOpenDocument: (id: string) => void;
}

/**
 * Read-first card for a person, mirroring the document/obligation
 * previews: tapping a person anywhere opens *this*, not a form.
 * Everything known about them in one place — relationship, who they're
 * linked to, their occasions (tap → that entry), their documents
 * (tap → the Documents by-person view), and an inline note.
 *
 * Renders one z-layer above the other preview cards and edit drawers
 * (`--person` modifiers) because it can be opened *from* those surfaces
 * — a person chip on a document card, "View person" in the obligation
 * drawer — and must stack on top of its opener.
 */
export function PersonPreview({
  person,
  onClose,
  onEdit,
  onOpenOccasion,
  onOpenDocument,
}: Props) {
  const people = usePeople();
  const obligations = useObligations();
  const documents = useDocuments();
  const me = useCurrentUser();
  const update = useUpdatePerson();
  const navigate = useNavigate();
  const now = new Date(useNowMinute());

  const occasions = personOccasions(obligations.data ?? [], person.id);
  const docs = personDocuments(documents.data ?? [], person.id);
  // The curated shortlist: starred (key) documents about them —
  // passport, vaccination record. The full set stays one tap away.
  const keyDocs = docs.filter((d) => d.starred);
  const isMe = me.data?.personId === person.id;

  const relatedNames = person.relatedPersonIds
    .map((id) => people.data?.find((p) => p.id === id)?.displayName)
    .filter((n): n is string => Boolean(n));

  // Inline-editable notes, flushed on blur / dismiss — same behaviour
  // as the document preview.
  const [notesDraft, setNotesDraft] = useState(person.notes ?? '');
  const notesRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setNotesDraft(person.notes ?? '');
  }, [person.id, person.notes]);

  useEffect(() => {
    const el = notesRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [notesDraft]);

  function commitNotes() {
    const next = notesDraft.trim();
    const current = (person.notes ?? '').trim();
    if (next === current) return;
    update.mutate({ id: person.id, input: { notes: next || null } });
  }

  function closeWithSave() {
    commitNotes();
    onClose();
  }

  function handleEdit() {
    commitNotes();
    onEdit();
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeWithSave();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // closeWithSave reads the latest notesDraft via closure each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesDraft, person.id]);

  function openDocuments() {
    commitNotes();
    navigate(`/documents?person=${encodeURIComponent(person.id)}`);
  }

  // Occasion / key-document rows drill in *in place* (the host swaps
  // this card for that entry's preview and back). Only "all documents"
  // deliberately leaves for the Documents by-person view.
  function openOccasion(id: string) {
    commitNotes();
    onOpenOccasion(id);
  }

  function openDocument(id: string) {
    commitNotes();
    onOpenDocument(id);
  }

  const nextOccasion = occasions[0];
  const docTitles = docs
    .slice(0, 2)
    .map((d) => d.title)
    .join(', ');
  const firstName = person.displayName.trim().split(/\s+/u)[0] ?? person.displayName;

  return (
    <>
      <div className="preview-scrim preview-scrim--person" onClick={closeWithSave} aria-hidden />
      <div
        className="preview-card preview-card--person"
        role="dialog"
        aria-modal="true"
        aria-label={`Details for ${person.displayName}`}
      >
        <button
          type="button"
          className="preview-card__close"
          onClick={closeWithSave}
          aria-label="Close"
        >
          ×
        </button>

        <div className="preview-card__header">
          <div className="person-card__head">
            <PersonAvatar person={person} className="person-card__av" />
            <div>
              <h2 className="preview-card__title">{person.displayName}</h2>
              {(person.relationship || isMe) && (
                <div className="preview-card__subtitle">
                  {person.relationship && capitalize(person.relationship)}
                  {isMe && (
                    <>
                      {person.relationship && ' '}
                      <span className="shared-pill">You</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="preview-card__facts">
          {relatedNames.length > 0 && (
            <div className="preview-card__fact">
              <span className="preview-card__fact-label">Of</span>
              <span className="preview-card__fact-value">{relatedNames.join(', ')}</span>
            </div>
          )}
          {nextOccasion && (
            <div className="preview-card__fact">
              <span className="preview-card__fact-label">Next up</span>
              <span className="preview-card__fact-value preview-card__fact-value--emphasize">
                {nextUpLabel(nextOccasion)}
              </span>
            </div>
          )}
        </div>

        {occasions.length > 0 && (
          <div className="preview-card__file">
            <div className="preview-card__section-label">Occasions</div>
            <div className="person-card__rows">
              {occasions.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="person-card__row"
                  onClick={() => openOccasion(o.id)}
                >
                  <span className="person-card__row-text">{describeOccasion(o)}</span>
                  <ChevronRight />
                </button>
              ))}
            </div>
          </div>
        )}

        {keyDocs.length > 0 ? (
          // Curated view: the starred key documents (passport,
          // vaccination record…) as rows, full set behind a link.
          <div className="preview-card__file">
            <div className="preview-card__section-label">Key documents</div>
            <div className="person-card__rows">
              {keyDocs.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className="person-card__row"
                  onClick={() => openDocument(d.id)}
                >
                  <StarGlyph />
                  <span className="person-card__row-text">
                    <strong>{d.title}</strong>
                    {keyDocDetail(d, now) && (
                      <span className="person-card__row-detail"> · {keyDocDetail(d, now)}</span>
                    )}
                  </span>
                  <ChevronRight />
                </button>
              ))}
            </div>
            <button type="button" className="person-card__alldocs" onClick={openDocuments}>
              All {docs.length} {docs.length === 1 ? 'document' : 'documents'} ›
            </button>
          </div>
        ) : docs.length > 0 ? (
          <div className="preview-card__file">
            <div className="preview-card__section-label">Documents</div>
            <button type="button" className="preview-card__file-open" onClick={openDocuments}>
              <span className="preview-card__file-open-icon" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 3v5h5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="preview-card__file-open-meta">
                <span className="preview-card__file-open-name">
                  {docs.length} {docs.length === 1 ? 'document' : 'documents'} about {firstName}
                </span>
                <span className="preview-card__file-open-sub">
                  {docTitles ? `${docTitles}… · ` : ''}Tap to view
                </span>
              </span>
            </button>
          </div>
        ) : null}

        <textarea
          ref={notesRef}
          className="preview-card__notes preview-card__notes--editable"
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          onBlur={commitNotes}
          placeholder="Add a note…"
          rows={1}
          aria-label="Notes"
        />

        <div className="preview-card__timestamps">
          <span>Added {formatStamp(person.createdAt)}</span>
          <span>Last updated {formatStamp(person.updatedAt)}</span>
        </div>

        <footer className="preview-card__footer">
          {docs.length > 0 ? (
            <button type="button" className="btn btn--secondary btn--small" onClick={openDocuments}>
              All documents
            </button>
          ) : (
            <span />
          )}
          <button type="button" className="btn btn--primary btn--small" onClick={handleEdit}>
            Edit
          </button>
        </footer>
      </div>
    </>
  );
}

/** "Turns 3 on 29 November" / "Birthday on 14 March" / "Anniversary on 12 June". */
function nextUpLabel(o: {
  occasionType: string | null;
  occasionLabel: string | null;
  anchorYear: number | null;
  nextDueDate: string | null;
  dueDate: string;
}): string {
  const next = (o.nextDueDate ?? o.dueDate).slice(0, 10);
  const d = new Date(`${next}T00:00:00`);
  const when = Number.isNaN(d.getTime())
    ? next
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
  const nextYear = o.nextDueDate ? Number(o.nextDueDate.slice(0, 4)) : null;
  const years = nextYear && o.anchorYear ? nextYear - o.anchorYear : null;
  if (o.occasionType === 'anniversary') {
    const label = o.occasionLabel?.trim() || 'Anniversary';
    return `${capitalize(label)} on ${when}`;
  }
  if (years && years > 0) return `Turns ${years} on ${when}`;
  return `Birthday on ${when}`;
}

function capitalize(s: string): string {
  return s.length > 0 ? s[0]!.toUpperCase() + s.slice(1) : s;
}

/** One-line context for a key-document row — its expiry when it has
 *  one (the fact that matters for a passport), otherwise its kind. */
function keyDocDetail(d: DocumentDTO, now: Date): string {
  if (d.expiryDate) {
    const date = new Date(`${d.expiryDate.slice(0, 10)}T00:00:00`);
    if (!Number.isNaN(date.getTime())) {
      const label = date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
      return date < now ? `expired ${label}` : `expires ${label}`;
    }
  }
  return d.category ?? '';
}

function StarGlyph() {
  return (
    <svg
      className="person-card__row-star"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12 2l2.9 6.26 6.6.7-4.9 4.5 1.35 6.54L12 16.9 6.05 20l1.35-6.54-4.9-4.5 6.6-.7z" />
    </svg>
  );
}

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function ChevronRight() {
  return (
    <svg
      className="person-card__row-chev"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
